import { Control, Controller, FieldErrors } from 'react-hook-form';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { PartnerCompanySelect } from '@/features/partner-companies/components/PartnerCompanySelect';
import type { ProviderEditorFormData } from '../schemas';
import type { PartnerCompanyOption } from '@/features/partner-companies/types/partner-company.types';

type AccountManagementSectionProps = {
  control: Control<ProviderEditorFormData>;
  errors: FieldErrors<ProviderEditorFormData>;
  userId?: string;
  kindeId?: string;
  partnerCompany?: PartnerCompanyOption | null;
  onPartnerCompanyChange?: (company: PartnerCompanyOption | null) => void;
};

type FormFieldProps = {
  label: string;
  required?: boolean;
  error?: string;
  htmlFor?: string;
  children: React.ReactNode;
  hint?: string;
};

/**
 * Reusable form field wrapper with consistent styling
 */
function FormField({
  label,
  required = false,
  error,
  htmlFor,
  children,
  hint,
}: FormFieldProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor} className="text-sm font-medium text-gray-700">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </Label>
      {children}
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

/**
 * AccountManagementSection - Displays user account information and status controls
 *
 * Features:
 * - Read-only user_id and kinde_id fields (displayed for admin reference)
 * - Editable account status dropdown (ACTIVE, INACTIVE, SUSPENDED, PENDING_VERIFICATION)
 * - Editable vetting status dropdown (PENDING, IN_REVIEW, APPROVED, REJECTED)
 * - Email field (required for account creation)
 * - First name and last name fields
 * - Membership tracking fields (membership date, renewal date)
 *
 * Technical notes:
 * - user_id and kinde_id are styled with gray background to indicate non-editable
 * - Account status changes trigger Kinde IDP sync (handled in parent form submit)
 * - Profile is visible to users only when vetting status = APPROVED AND account status = ACTIVE
 * - All user fields are under 'user.' namespace, provider fields under 'provider.' namespace
 */
export function AccountManagementSection({
  control,
  errors,
  userId,
  kindeId,
  partnerCompany,
  onPartnerCompanyChange,
}: AccountManagementSectionProps) {
  return (
    <div className="space-y-6">
      {/* Partner Company Assignment (T043 - US2) */}
      {onPartnerCompanyChange && (
        <FormField
          label="Partner Company"
          htmlFor="partner-company"
          hint="Assign user to a partner company for organizational affiliation"
        >
          <PartnerCompanySelect
            value={partnerCompany ?? null}
            onChange={onPartnerCompanyChange}
            placeholder="Select partner company..."
          />
        </FormField>
      )}

      {/* Read-only Account Identifiers */}
      {(userId || kindeId) && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <h4 className="mb-3 text-sm font-medium text-gray-700">
            Account Identifiers (Read-Only)
          </h4>
          <div className="grid gap-4 sm:grid-cols-2">
            {userId && (
              <FormField
                label="Internal User ID"
                htmlFor="user-id"
                hint="Application database identifier"
              >
                <Input
                  id="user-id"
                  value={userId}
                  disabled
                  className="bg-gray-100 text-gray-600"
                />
              </FormField>
            )}
            <FormField
              label="Kinde ID"
              htmlFor="kinde-id"
              hint="Kinde authentication identifier"
            >
              <Input
                id="kinde-id"
                value={kindeId}
                disabled
                className="bg-gray-100 text-gray-600"
              />
            </FormField>
          </div>
        </div>
      )}

      {/* Email Field */}
      <FormField
        label="Email"
        required
        htmlFor="user-email"
        error={errors.user?.email?.message}
        hint="Used for login and communications"
      >
        <Controller
          name="user.email"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              id="user-email"
              type="email"
              className={errors.user?.email ? 'border-red-500' : ''}
            />
          )}
        />
      </FormField>

      {/* Name Fields */}
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          label="First Name"
          required
          htmlFor="user-first-name"
          error={errors.user?.firstName?.message}
        >
          <Controller
            name="user.firstName"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                id="user-first-name"
                className={errors.user?.firstName ? 'border-red-500' : ''}
              />
            )}
          />
        </FormField>

        <FormField
          label="Last Name"
          htmlFor="user-last-name"
          error={errors.user?.lastName?.message}
        >
          <Controller
            name="user.lastName"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                value={field.value || ''}
                id="user-last-name"
                className={errors.user?.lastName ? 'border-red-500' : ''}
              />
            )}
          />
        </FormField>
      </div>

      {/* Account Status */}
      <FormField
        label="Account Status"
        htmlFor="user-account-status"
        error={errors.user?.accountStatus?.message}
        hint="Any status other than Active will hide profile from application users"
      >
        <Controller
          name="user.accountStatus"
          control={control}
          render={({ field }) => (
            <select
              {...field}
              id="user-account-status"
              value={field.value || 'ACTIVE'}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="SUSPENDED">Suspended</option>
              <option value="PENDING_VERIFICATION">Pending Verification</option>
            </select>
          )}
        />
      </FormField>

      {/* Vetting Status */}
      <FormField
        label="Vetting Status"
        htmlFor="vetting-status"
        error={errors.provider?.vettingStatus?.message}
        hint="Onboarding status of provider. Must be Approved for profile to be visible to application users"
      >
        <Controller
          name="provider.vettingStatus"
          control={control}
          render={({ field }) => (
            <select
              {...field}
              id="vetting-status"
              value={field.value || 'PENDING'}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="PENDING">Pending</option>
              <option value="IN_REVIEW">In Review</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
            </select>
          )}
        />
      </FormField>

      {/* Membership Tracking (Feature 011 - US4) */}
      <div className="rounded-lg border border-gray-200 bg-blue-50 p-4">
        <h4 className="mb-3 text-sm font-medium text-gray-700">
          Membership Tracking (Admin Only)
        </h4>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label="Membership Date"
            htmlFor="membership-date"
            error={errors.provider?.membershipDate?.message}
            hint="When provider membership started"
          >
            <Controller
              name="provider.membershipDate"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  id="membership-date"
                  type="date"
                  value={field.value ? new Date(field.value).toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value ? new Date(value) : null);
                  }}
                  className={errors.provider?.membershipDate ? 'border-red-500' : ''}
                />
              )}
            />
          </FormField>

          <FormField
            label="Renewal Date"
            htmlFor="renewal-date"
            error={errors.provider?.renewalDate?.message}
            hint="When membership needs renewal"
          >
            <Controller
              name="provider.renewalDate"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  id="renewal-date"
                  type="date"
                  value={field.value ? new Date(field.value).toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value ? new Date(value) : null);
                  }}
                  className={errors.provider?.renewalDate ? 'border-red-500' : ''}
                />
              )}
            />
          </FormField>
        </div>
      </div>
    </div>
  );
}

